"use client";
import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import * as flags from "country-flag-icons/react/3x2";
import { FiX } from "react-icons/fi";
import { BsBank2 } from "react-icons/bs";
import { MdEmail, MdOutlinePlace } from "react-icons/md";
import { FaPhoneAlt, FaRegIdBadge } from "react-icons/fa";
import { IoBusinessOutline } from "react-icons/io5";
import { LuGlobe } from "react-icons/lu";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";

const API_BASE = `${REST_API_URL}/invoice/bank-info/`;

const BankInfoModal = ({ onClose, onUpdated }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name_surname: "",
    company_name: "",
    tax_id: "",
    address: "",
    phone_number: "",
    email: "",
    iban: "",
    swift_bic: "",
  });

  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("US");
  const [countryCode, setCountryCode] = useState("+1");
  const [openCountries, setOpenCountries] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchBankInfo = async () => {
      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");

      if (!token) {
        toast.error(t("home.toastAuthTokenNotFound"));
        return;
      }

      try {
        setLoading(true);
        const headers = buildHeaders();

        const res = await fetch(API_BASE, {
          headers,
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          console.error("❌ Fetch error:", errData);
          toast.error(t("home.toastLoadBankInfoError", { status: res.status }));
          return;
        }

        const data = await res.json();
        setForm(data || {});
      } catch (err) {
        console.error("❌ Failed to load bank info:", err);
        toast.error(t("home.toastNetworkLoadError"));
      } finally {
        setLoading(false);
      }
    };

    fetchBankInfo();
  }, [t]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenCountries(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    const token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");

    if (!token) {
      toast.error(t("home.toastAuthTokenNotFound"));
      return;
    }

    try {
      setLoading(true);

      const rawPhone = (form.phone_number || "").trim();

      let fullPhone = "";
      if (rawPhone) {
        if (rawPhone.startsWith("+")) {
          fullPhone = rawPhone.replace(/\s+/g, "");
        } else {
          const clean = rawPhone.replace(/[^\d]/g, "");
          fullPhone = clean ? `${countryCode}${clean}` : "";
        }
      }

      const payload = {
        name_surname: form.name_surname?.trim() || "",
        company_name: form.company_name?.trim() || "",
        tax_id: form.tax_id?.trim() || "",
        address: form.address?.trim() || "",
        phone_number: fullPhone,
        email: form.email?.trim() || "",
        iban: form.iban?.trim() || "",
        swift_bic: form.swift_bic?.trim() || "",
      };

      const headers = buildHeaders();

      const res = await fetch(API_BASE, {
        method: "PUT",
        headers,
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        console.error("❌ Error:", errData);

        if (errData && typeof errData === "object") {
          const messages = [];
          Object.values(errData).forEach((val) => {
            if (Array.isArray(val)) messages.push(...val);
            else if (typeof val === "string") messages.push(val);
          });
          if (messages.length > 0) {
            messages.forEach((msg) => toast.error(msg));
            return;
          }
        }

        toast.error(t("home.toastSaveBankInfoError", { status: res.status }));
        return;
      }

      toast.success(t("home.toastUpdateSuccessBankInfo"));

      if (onUpdated) onUpdated();

      onClose();
    } catch (err) {
      console.error("❌ Fetch failed:", err);
      toast.error(t("home.toastNetworkSaveError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white border border-[#9A99AB33] shadow-md rounded-[16px] w-[92vw] max-w-[750px] max-h-[90vh] flex flex-col justify-between p-6 relative overflow-y-auto"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-[20px] font-bold text-[#302B2B]">
            {t("home.bankInfoTitle")}
          </h2>
          <button
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded-full"
          >
            <FiX className="text-[#302B2B]" size={20} />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-10 text-gray-500">
            {t("home.loadingText")}
          </div>
        ) : (
          <div className="space-y-4 overflow-y-auto pr-2">
            <div className="flex flex-col sm:flex-row gap-4">
              <InputField
                label={t("home.labelNameSurname")}
                icon={<FaRegIdBadge />}
                placeholder={t("home.placeholderNameSurname")}
                value={form.name_surname}
                onChange={(v) => handleChange("name_surname", v)}
              />
              <InputField
                label={t("home.labelCompanyName")}
                icon={<IoBusinessOutline />}
                placeholder={t("home.placeholderCompanyName")}
                value={form.company_name}
                onChange={(v) => handleChange("company_name", v)}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <InputField
                label={t("home.labelTaxId")}
                icon={<FaRegIdBadge />}
                placeholder={t("home.placeholderTaxId")}
                value={form.tax_id}
                onChange={(v) => handleChange("tax_id", v)}
              />
              <InputField
                label={t("home.labelAddress")}
                icon={<MdOutlinePlace />}
                placeholder={t("home.placeholderAddress")}
                value={form.address}
                onChange={(v) => handleChange("address", v)}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex flex-col flex-1 min-w-0 w-full" ref={dropdownRef}>
                <label className="text-[14px] text-[#302B2B] mb-1">
                  {t("phoneNumber")}
                </label>
                <div className="border border-[#9A99AB] rounded-[14px] flex items-center gap-2 px-3 py-3">
                  <div
                    className="flex items-center gap-2 cursor-pointer bg-[#EDEEF3]/70 rounded-[14px] px-2 py-1"
                    onClick={() => setOpenCountries(!openCountries)}
                  >
                    {React.createElement(flags[selectedCountry] || flags.US, {
                      title: selectedCountry,
                      style: { width: 24, height: 18 },
                    })}
                    <span className="text-[#302B2B] text-[14px] font-medium">
                      {countryCode}
                    </span>
                  </div>
                  <input
                    type="tel"
                    placeholder={t("home.placeholderPhoneNumber")}
                    value={form.phone_number}
                    onChange={(e) =>
                      handleChange("phone_number", e.target.value)
                    }
                    className="flex-1 min-w-0 outline-none text-[16px] text-[#9A99AB]"
                  />
                </div>

                {openCountries && (
                  <div className="absolute mt-1 max-h-48 overflow-y-auto w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    {getCountries().map((c) => (
                      <div
                        key={c}
                        className="flex justify-between items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => {
                          setSelectedCountry(c);
                          setCountryCode("+" + getCountryCallingCode(c));
                          setOpenCountries(false);
                        }}
                      >
                        <div className="flex items-center gap-2">
                          {React.createElement(flags[c] || flags.US, {
                            title: c,
                            style: { width: 20, height: 14 },
                          })}
                          <span>{c}</span>
                        </div>
                        <span>+{getCountryCallingCode(c)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <InputField
                label={t("home.labelEmail")}
                icon={<MdEmail />}
                placeholder={t("home.placeholderEmail")}
                value={form.email}
                onChange={(v) => handleChange("email", v)}
              />
            </div>

            {/* Row 4 */}
            <div className="flex flex-col sm:flex-row gap-4">
              <InputField
                label={t("home.labelIban")}
                icon={<BsBank2 />}
                placeholder={t("home.placeholderIban")}
                value={form.iban}
                onChange={(v) => handleChange("iban", v)}
              />
              <InputField
                label={t("home.labelSwiftBic")}
                icon={<LuGlobe />}
                placeholder={t("home.placeholderSwiftBic")}
                value={form.swift_bic}
                onChange={(v) => handleChange("swift_bic", v)}
              />
            </div>
          </div>
        )}

        <div className="flex justify-end items-center gap-4 mt-6">
          <button
            onClick={onClose}
            className="text-[#9A99AB] text-[16px] hover:text-[#302B2B]"
          >
            {t("home.cancelButton")}
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="bg-[#020637] text-white px-8 py-3 rounded-[14px] hover:opacity-90 font-medium disabled:opacity-50"
          >
            {loading ? t("home.savingStatus") : t("home.saveButton")}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const InputField = ({ label, icon, placeholder, value, onChange }) => (
  <div className="flex flex-col flex-1 min-w-0 w-full">
    <label className="text-[14px] text-[#302B2B] mb-1">{label}</label>
    <div className="border border-[#9A99AB] rounded-[14px] flex items-center gap-2 px-3 py-3">
      <span className="text-[#302B2B] opacity-70">{icon}</span>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 min-w-0 outline-none text-[16px] text-[#9A99AB]"
      />
    </div>
  </div>
);

export default BankInfoModal;
